import { useMemo, useState } from 'react';
import { LockKeyhole, Settings2 } from 'lucide-react';

import {
  useCostCodes,
  useProjectUsers,
  usePunchListItemTypes,
  useTrades,
  useTradePartners,
} from '@/features/punch-lists/hooks';
import type {
  QuickPunchDefaultValue,
  QuickPunchFieldDefinition,
  QuickPunchFieldKey,
} from '@/features/punch-lists/types';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { cn } from '@/shared/utils/utils';

type Defaults = Partial<Record<QuickPunchFieldKey, QuickPunchDefaultValue>>;

interface QuickPunchConfigurationEditorProps {
  projectId: number;
  fields: QuickPunchFieldDefinition[];
  mode: 'user' | 'project';
  selectedFields: QuickPunchFieldKey[];
  requiredFields?: QuickPunchFieldKey[];
  defaults: Defaults;
  lockedFields?: QuickPunchFieldKey[];
  lockedDefaultFields?: QuickPunchFieldKey[];
  submitLabel: string;
  isSaving?: boolean;
  onCancel?: () => void;
  onSave: (value: {
    selectedFields: QuickPunchFieldKey[];
    requiredFields: QuickPunchFieldKey[];
    defaults: Defaults;
  }) => void | Promise<void>;
}

const PRIORITIES = [
  ['LOW', 'Low'],
  ['MEDIUM', 'Medium'],
  ['HIGH', 'High'],
] as const;

const IMPACTS = [
  ['YES', 'Yes'],
  ['YES_UNKNOWN', 'Yes (Unknown)'],
  ['NO', 'No'],
  ['TBD', 'TBD'],
  ['NA', 'N/A'],
] as const;

const displayUser = (user: { first_name: string; last_name: string; email: string }) =>
  `${user.first_name} ${user.last_name}`.trim() || user.email;

export const QuickPunchConfigurationEditor = ({
  projectId,
  fields,
  mode,
  selectedFields: initialSelectedFields,
  requiredFields: initialRequiredFields = [],
  defaults: initialDefaults,
  lockedFields = [],
  lockedDefaultFields = [],
  submitLabel,
  isSaving = false,
  onCancel,
  onSave,
}: QuickPunchConfigurationEditorProps) => {
  const [selectedFields, setSelectedFields] = useState<QuickPunchFieldKey[]>(initialSelectedFields);
  const [requiredFields, setRequiredFields] = useState<QuickPunchFieldKey[]>(initialRequiredFields);
  const [defaults, setDefaults] = useState<Defaults>(initialDefaults);
  const { data: itemTypes = [] } = usePunchListItemTypes();
  const { data: trades = [] } = useTrades();
  const { data: tradePartners = [] } = useTradePartners(projectId);
  const { data: users = [] } = useProjectUsers(projectId);
  const { data: costCodes = [] } = useCostCodes();

  const locked = useMemo(() => new Set(lockedFields), [lockedFields]);
  const lockedDefaults = useMemo(() => new Set(lockedDefaultFields), [lockedDefaultFields]);
  const selected = useMemo(() => new Set(selectedFields), [selectedFields]);
  const required = useMemo(() => new Set(requiredFields), [requiredFields]);

  const toggleField = (key: QuickPunchFieldKey, checked: boolean) => {
    if (locked.has(key) || key === 'description' || required.has(key)) return;
    setSelectedFields((current) =>
      checked ? [...current, key] : current.filter((field) => field !== key)
    );
  };

  const toggleRequired = (key: QuickPunchFieldKey, checked: boolean) => {
    if (locked.has(key) || key === 'description') return;
    setRequiredFields((current) =>
      checked ? [...current, key] : current.filter((field) => field !== key)
    );
    if (checked) {
      setSelectedFields((current) => (current.includes(key) ? current : [...current, key]));
    }
  };

  const setDefault = (key: QuickPunchFieldKey, value: QuickPunchDefaultValue | undefined) => {
    setDefaults((current) => {
      const next = { ...current };
      if (value === undefined || value === '') delete next[key];
      else next[key] = value;
      return next;
    });
  };

  const selectControl = (
    field: QuickPunchFieldDefinition,
    options: Array<{ value: string; label: string }>
  ) => (
    <Select
      value={defaults[field.key] == null ? 'no-default' : String(defaults[field.key])}
      onValueChange={(value) => setDefault(field.key, value === 'no-default' ? undefined : value)}
      disabled={lockedDefaults.has(field.key)}
    >
      <SelectTrigger aria-label={`${field.label} default`}>
        <SelectValue placeholder="No default" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="no-default">No default</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const renderDefaultControl = (field: QuickPunchFieldDefinition) => {
    if (!field.supports_default) {
      return <p className="text-xs text-muted-foreground">Entered for every item.</p>;
    }
    switch (field.kind) {
      case 'text':
        return (
          <Input
            aria-label={`${field.label} default`}
            value={String(defaults[field.key] ?? '')}
            onChange={(event) => setDefault(field.key, event.target.value || undefined)}
            placeholder="No default"
            disabled={lockedDefaults.has(field.key)}
          />
        );
      case 'priority':
        return selectControl(
          field,
          PRIORITIES.map(([value, label]) => ({ value, label }))
        );
      case 'impact':
        return selectControl(
          field,
          IMPACTS.map(([value, label]) => ({ value, label }))
        );
      case 'item_type':
        return selectControl(
          field,
          itemTypes.map((item) => ({ value: item.id, label: item.name }))
        );
      case 'trade':
        return selectControl(
          field,
          trades.map((trade) => ({ value: trade.id, label: trade.name }))
        );
      case 'trade_partner':
        return selectControl(
          field,
          tradePartners.map((partner) => ({
            value: String(partner.id),
            label: partner.company_name,
          }))
        );
      case 'project_user':
        return selectControl(
          field,
          users.map((user) => ({ value: String(user.id), label: displayUser(user) }))
        );
      case 'cost_code':
        return selectControl(
          field,
          costCodes.map((code) => ({ value: code.id, label: `${code.code} – ${code.name}` }))
        );
      case 'datetime':
        return (
          <Input
            aria-label={`${field.label} default`}
            type="datetime-local"
            value={String(defaults[field.key] ?? '')}
            onChange={(event) => setDefault(field.key, event.target.value || undefined)}
            disabled={lockedDefaults.has(field.key)}
          />
        );
      case 'boolean':
        return selectControl(field, [
          { value: 'false', label: 'No' },
          { value: 'true', label: 'Yes' },
        ]);
      default:
        return null;
    }
  };

  const normalizedDefaults = Object.fromEntries(
    Object.entries(defaults).map(([key, value]) => [
      key,
      key === 'is_private' ? value === true || value === 'true' : value,
    ])
  ) as Defaults;

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 rounded-[13px] border border-primary/20 bg-primary/10 p-4">
        <Settings2 className="mt-0.5 size-5 shrink-0 text-primary" strokeWidth={1.9} />
        <div>
          <p className="text-sm font-semibold text-foreground">
            {mode === 'project' ? 'Project Quick Punch policy' : 'Build your Quick Punch form'}
          </p>
          <p
            className={cn(
              'mt-1 leading-6 text-muted-foreground',
              mode === 'user' ? 'text-sm sm:text-base' : 'text-sm'
            )}
          >
            {mode === 'project'
              ? 'Required fields and project defaults are locked for everyone submitting Quick Punch items.'
              : 'Check the fields you want to enter. Leave a field unchecked and give it a default to apply it automatically without showing it on the form. Save when you are ready to preview the finished form.'}
          </p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {fields.map((field) => {
          const hasEnforcedDefault = mode === 'project' && defaults[field.key] != null;
          const isRequiredLocked =
            locked.has(field.key) || field.system_required || hasEnforcedDefault;
          const isRequired = required.has(field.key) || isRequiredLocked;
          const isSelected = selected.has(field.key) || isRequired;
          const isShowLocked = isRequired || locked.has(field.key) || field.system_required;
          const isProjectControlled = mode === 'user' && lockedDefaults.has(field.key);
          return (
            <div
              key={field.key}
              className={cn(
                'flex min-h-[168px] flex-col rounded-[13px] border bg-card p-4 transition-colors',
                isSelected ? 'border-primary/25' : 'border-border'
              )}
            >
              <div className="flex items-start gap-3 border-b border-border pb-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">{field.label}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {field.system_required && <Badge variant="secondary">Quick Punch core</Badge>}
                    {locked.has(field.key) && (
                      <Badge className="gap-1 border-primary/25 bg-primary/12 text-primary">
                        <LockKeyhole className="size-3" /> Project locked
                      </Badge>
                    )}
                    {mode === 'project' && defaults[field.key] != null && (
                      <Badge variant="secondary">Default is enforced</Badge>
                    )}
                  </div>
                </div>
                <Badge variant={isRequired || isSelected ? 'default' : 'secondary'}>
                  {isRequired ? 'Required' : isSelected ? 'Included' : 'Excluded'}
                </Badge>
              </div>
              <div
                className={cn(
                  'grid gap-2 border-b border-border py-3',
                  mode === 'user' ? 'grid-cols-2' : 'grid-cols-1'
                )}
              >
                {mode === 'user' && (
                  <div className="flex items-center gap-2 rounded-[10px] bg-muted px-3 py-2">
                    <Checkbox
                      id={`quick-punch-${mode}-${field.key}-show`}
                      aria-label={`Show ${field.label}`}
                      checked={isSelected}
                      disabled={isShowLocked}
                      onCheckedChange={(checked) => toggleField(field.key, checked === true)}
                    />
                    <Label htmlFor={`quick-punch-${mode}-${field.key}-show`}>Show</Label>
                  </div>
                )}
                <div className="flex items-center gap-2 rounded-[10px] bg-muted px-3 py-2">
                  <Checkbox
                    id={`quick-punch-${mode}-${field.key}-required`}
                    aria-label={`Require ${field.label}`}
                    checked={isRequired}
                    disabled={isRequiredLocked}
                    onCheckedChange={(checked) => toggleRequired(field.key, checked === true)}
                  />
                  <Label htmlFor={`quick-punch-${mode}-${field.key}-required`}>Required</Label>
                </div>
              </div>
              <div
                className={cn(
                  'mt-auto pt-4',
                  isProjectControlled && 'pointer-events-none opacity-65'
                )}
              >
                <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                  Default value
                </p>
                {renderDefaultControl(field)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end gap-2 pt-1">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          type="button"
          disabled={isSaving}
          onClick={() =>
            void onSave({
              selectedFields: fields
                .map((field) => field.key)
                .filter(
                  (key) =>
                    selected.has(key) ||
                    required.has(key) ||
                    locked.has(key) ||
                    key === 'description'
                ),
              requiredFields: fields
                .map((field) => field.key)
                .filter((key) => required.has(key) || locked.has(key) || key === 'description'),
              defaults: normalizedDefaults,
            })
          }
        >
          {isSaving ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </div>
  );
};
